<script setup lang="ts">
import { onUnmounted } from 'vue';

import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import TablePageFooter from '@/components/footers/TablePageFooter.vue';
import ExportAsCsvIconBtn from '@/components/iconBtns/ExportAsCsvIconBtn.vue';
import JobsAirRoadSearchForm from '@/components/searchForms/JobsAirRoadSearchForm.vue';
import JobsAirRoadTable from '@/components/tables/JobsAirRoadTable.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import TablePageShadowWrapper from '@/components/wrappers/TablePageShadowWrapper.vue';
import useJobsAirRoadViewData from '@/composables/data/useJobsAirRoadViewData';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import { useJobsAirRoadViewStore } from '@/stores/useJobsAirRoadViewStore';
import * as routeNames from '@/constants/routeNames';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

const { getLast7DaysNext5DaysRange } = useCurrentDateString();
const routeQuery = useCommonRouteQuery({
  queryCurPage: defCurPage,
  queryPageSize: defPageSize,
  queryDates: getLast7DaysNext5DaysRange(),
  queryOnly920Driver: true,
  queryExclude920Driver: false,
  queryFromWarehouse: false,
  queryToWarehouse: false,
});

const jobsAirRoadViewStore = useJobsAirRoadViewStore();
jobsAirRoadViewStore.init(useJobsAirRoadViewData(routeQuery), routeQuery);

onUnmounted(function () {
  jobsAirRoadViewStore.$reset();
});
</script>

<template>
  <div :class="$style.jobsAirRoadView" data-testid="jobsAirRoadView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <JobsAirRoadSearchForm
          :initial-data="jobsAirRoadViewStore.initialData"
          :loading="jobsAirRoadViewStore.data.isFetchingAirRoadBookingJobsData"
          :class="$style.form"
          @submit="jobsAirRoadViewStore.search"
        />
      </FormWrapper>
      <TablePageShadowWrapper :class="$style.tablePageShadowWrapper">
        <JobsAirRoadTable
          v-model="jobsAirRoadViewStore.selectedResultId"
          :data="jobsAirRoadViewStore.jobsAirRoadTableData"
          :loading="jobsAirRoadViewStore.data.isFetchingAirRoadBookingJobsData"
        />
        <TablePageFooter
          v-if="jobsAirRoadViewStore.data.airRoadBookingJobsData?.results"
          :cur-page="jobsAirRoadViewStore.query.curPage"
          :page-size="jobsAirRoadViewStore.query.pageSize"
          :total="jobsAirRoadViewStore.data.airRoadBookingJobsData.count"
        >
          <ExportAsCsvIconBtn
            :disabled="
              jobsAirRoadViewStore.data.isFetchingAirRoadBookingJobsData ||
              !jobsAirRoadViewStore.data.airRoadBookingJobsData?.results?.length
            "
            :loading="jobsAirRoadViewStore.isDownloadingCSV"
            @click="jobsAirRoadViewStore.downloadCSV"
          />
        </TablePageFooter>
      </TablePageShadowWrapper>
    </PageContentWrapper>
    <TableFooterBar>
      <KBtn
        size="lg"
        append-icon="mdi-pencil-outline"
        :disabled="
          jobsAirRoadViewStore.data.isFetchingAirRoadBookingJobsData ||
          !jobsAirRoadViewStore.data.airRoadBookingJobsData?.results?.length ||
          !jobsAirRoadViewStore.selectedJobIdAndJobDate
        "
        :to="{
          name: routeNames.AIR_ROAD_JOBS_NEW,
          query: {
            job_id: jobsAirRoadViewStore.selectedJobIdAndJobDate?.jobId,
            job_date: jobsAirRoadViewStore.selectedJobIdAndJobDate?.jobDate,
          },
        }"
      >
        Book
      </KBtn>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobsAirRoadView {
}
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
    margin: 2rem 0;
  }
  .tablePageShadowWrapper {
    max-width: calc(100vw - variables.$sideMainBarWidth - (variables.$pageMarginX * 2));
  }
</style>
